import { useEffect, useState } from "react";
import { marked } from "marked";

/**
 * Renders a markdown file fetched from `public/`.
 *
 * Content lives as raw `.md` files at stable URLs (`/quickstart-python.md`
 * etc.) so the same artifacts power both human readers and LLM agents
 * consuming llms.txt. The SPA route fetches + renders them client-side.
 */
export function Markdown({ path }: { path: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    fetch(path)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.text();
      })
      .then(async (text) => {
        if (cancelled) return;
        const rendered = await marked.parse(text, {
          gfm: true,
          breaks: false,
        });
        setHtml(rendered);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (error) {
    return <p className="text-sm text-red-500">Failed to load: {error}</p>;
  }
  if (html === null) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-2/3 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
        <div className="h-4 w-full animate-pulse rounded bg-neutral-100 dark:bg-neutral-900" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-neutral-100 dark:bg-neutral-900" />
      </div>
    );
  }
  return (
    <article
      className="prose max-w-none"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
